import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { TrendingUp, TrendingDown, DollarSign, PieChart as PieChartIcon, Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import { toast } from 'sonner'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts'
import { useGestor } from '@/contexts/GestorContext'

interface OrcamentoResumo {
  ano: number
  valor_total_orcado: number
  valor_movimentado_saida: number
  valor_movimentado_entrada: number
  valor_contratado: number
  valor_verba_adicional: number
  valor_saldo_disponivel: number
  centro_resultado: string
  tipo_receita_despesa: string
}

const COLORS = ['#f97316', '#131E29', '#3b82f6', '#f59e0b', '#8b5cf6', '#64748b']

export default function GestorDashboardPage() {
  const { areaSelecionada, anoSelecionado } = useGestor()
  const [data, setData] = useState<OrcamentoResumo[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (!anoSelecionado || !areaSelecionada) return
    
    setLoading(true)
    try {
      const { data: viewData, error } = await supabase
        .from('solis_vw_orcamento_por_tipo_centro')
        .select('*')
        .eq('ano', parseInt(anoSelecionado))
        .eq('centro_resultado', areaSelecionada)

      if (error) throw error
      setData(viewData || [])
    } catch (error) {
      console.error('Erro ao buscar dados do orçamento:', error)
      toast.error('Erro ao carregar dados do orçamento.')
    } finally {
      setLoading(false)
    }
  }, [anoSelecionado, areaSelecionada])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0
    }).format(value)
  }

  // Cálculos agregados
  const totalOrcado = data.reduce((sum, item) => sum + Number(item.valor_total_orcado || 0), 0)
  const totalContratado = data.reduce((sum, item) => sum + Number(item.valor_contratado || 0), 0)
  const totalDisponivel = data.reduce((sum, item) => sum + Number(item.valor_saldo_disponivel || 0), 0)
  const totalAdicional = data.reduce((sum, item) => sum + Number(item.valor_verba_adicional || 0), 0)

  const percentualUtilizado = totalOrcado > 0 ? (totalContratado / totalOrcado) * 100 : 0
  const isOverBudget = totalDisponivel < 0

  // Dados para o gráfico de barras
  const barChartData = data.map(item => ({
    name: item.tipo_receita_despesa || 'Outros',
    Orcado: Number(item.valor_total_orcado || 0),
    Comprometido: Number(item.valor_contratado || 0),
  })).sort((a, b) => b.Orcado - a.Orcado).slice(0, 8)

  // Dados para o gráfico de pizza (Distribuição de gastos)
  const pieChartData = data
    .filter(item => Number(item.valor_contratado) > 0)
    .map(item => ({
      name: item.tipo_receita_despesa || 'Outros',
      value: Number(item.valor_contratado || 0)
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5)

  // Mock data for the area chart (simulating monthly spend)
  const monthlyData = [
    { name: 'Jan', value: totalContratado * 0.05 },
    { name: 'Fev', value: totalContratado * 0.12 },
    { name: 'Mar', value: totalContratado * 0.18 },
    { name: 'Abr', value: totalContratado * 0.25 },
    { name: 'Mai', value: totalContratado * 0.35 },
    { name: 'Jun', value: totalContratado * 0.45 },
    { name: 'Jul', value: totalContratado * 0.60 },
    { name: 'Ago', value: totalContratado * 0.75 },
    { name: 'Set', value: totalContratado * 0.85 },
    { name: 'Out', value: totalContratado * 0.90 },
    { name: 'Nov', value: totalContratado * 0.95 },
    { name: 'Dez', value: totalContratado },
  ]

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#131E29]/20 border-t-orange-500 border-r-[#131E29]" />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-none shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-500">Orçamento Total</p>
              <div className="p-2 bg-blue-50 rounded-lg">
                <DollarSign className="h-4 w-4 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-3xl font-bold text-slate-800">{formatCurrency(totalOrcado)}</h3>
              <div className="flex items-center gap-1 mt-1 text-sm">
                <span className="text-orange-600 flex items-center font-medium">
                  <ArrowUpRight className="h-3 w-3 mr-0.5" />
                  {formatCurrency(totalAdicional)}
                </span>
                <span className="text-slate-400">em verba adicional</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-500">Valor Comprometido</p>
              <div className="p-2 bg-orange-50 rounded-lg">
                <Activity className="h-4 w-4 text-orange-600" />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-3xl font-bold text-slate-800">{formatCurrency(totalContratado)}</h3>
              <div className="flex items-center gap-1 mt-1 text-sm">
                <span className="text-orange-600 font-medium">{percentualUtilizado.toFixed(1)}%</span>
                <span className="text-slate-400">do orçamento utilizado</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-500">Saldo Disponível</p>
              <div className={`p-2 rounded-lg ${isOverBudget ? 'bg-red-50' : 'bg-orange-50'}`}>
                <PieChartIcon className={`h-4 w-4 ${isOverBudget ? 'text-red-600' : 'text-orange-600'}`} />
              </div>
            </div>
            <div className="mt-4">
              <h3 className={`text-3xl font-bold ${isOverBudget ? 'text-red-600' : 'text-slate-800'}`}>
                {formatCurrency(totalDisponivel)}
              </h3>
              <div className="flex items-center gap-1 mt-1 text-sm">
                <span className={isOverBudget ? 'text-red-600 font-medium' : 'text-orange-700 font-medium'}>
                  {isOverBudget ? 'Déficit no orçamento' : 'Saldo positivo'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-sm bg-gradient-to-br from-[#131E29] to-slate-800 text-white ring-1 ring-black/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-300">Saúde Financeira</p>
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <TrendingUp className="h-4 w-4 text-orange-400" />
              </div>
            </div>
            <div className="mt-4">
              <h3 className="text-3xl font-bold text-white">
                {isOverBudget ? 'Atenção' : 'Saudável'}
              </h3>
              <div className="flex items-center gap-1 mt-1 text-sm text-slate-400">
                <span>{isOverBudget ? 'Necessita de revisão' : 'Dentro do planejado'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 md:grid-cols-3">
        
        {/* Bar Chart */}
        <Card className="md:col-span-2 border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800">Orçado vs Comprometido por Categoria</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickFormatter={(value) => `R$${(value / 1000)}k`}
                  />
                  <Tooltip 
                    cursor={{ fill: '#f8fafc' }}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    formatter={(value: number) => formatCurrency(value)}
                  />
                  <Bar dataKey="Orcado" name="Orçado" fill="#e2e8f0" radius={[4, 4, 4, 4]} barSize={12} />
                  <Bar dataKey="Comprometido" name="Comprometido" fill="#f97316" radius={[4, 4, 4, 4]} barSize={12} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart */}
        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800">Distribuição de Gastos</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center">
            <div className="h-[220px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                    stroke="none"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full space-y-2 mt-2">
              {pieChartData.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                    <span className="text-slate-600 truncate max-w-[120px]">{item.name}</span>
                  </div>
                  <span className="font-medium text-slate-800">{((item.value / totalContratado) * 100).toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Area Chart (Evolução) */}
        <Card className="md:col-span-3 border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800">Evolução do Comprometimento (Acumulado)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px] w-full mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f97316" stopOpacity={0.35}/>
                      <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickFormatter={(value) => `R$${(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Area type="monotone" dataKey="value" stroke="#f97316" strokeWidth={3} fillOpacity={1} fill="url(#colorValue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  )
}
